import { useEffect, useState } from 'react';
import { useIsTutorialMode } from '@context/PatientDataContext';
import { useAccessToken } from '@features/user';
import APIService from '@services/APIService';

import type { Patient } from '../types/types';

// Type for the API response
interface PatientApiResponse {
  patient?: Patient;
  status?: string;
}

export const usePatientById = (patientId?: string) => {
  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const { getAccessToken } = useAccessToken();
  const isTutorialMode = useIsTutorialMode();

  useEffect(() => {
    if (!patientId || isTutorialMode) {
      setPatient(undefined);
      return;
    }

    const fetchPatient = async () => {
      setLoading(true);
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/patient/get?patient_id=${encodeURIComponent(patientId)}`,
          accessToken,
        });

        if (response.ok) {
          const data = (await response.value) as PatientApiResponse | Patient;
          // The API returns { patient: {...}, status: ... }, so extract the patient
          if ('patient' in data && data.patient) {
            setPatient(data.patient);
          } else {
            // Fallback: if the response is directly a Patient object
            setPatient(data as Patient);
          }
        }
      } catch (error) {
        console.error('Failed to load patient:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [patientId, getAccessToken, isTutorialMode]);

  return { patient, loading };
};
